import React from 'react';
import { Modal, Button, Progress } from 'antd';
import { IPopup } from '@/services/popups/types';
import {
  CloseOutlined,
  LeftOutlined,
  RightOutlined,
  FastForwardOutlined,
} from '@ant-design/icons';

interface PopupNotificationProps {
  popup: IPopup | null;
  currentIndex: number;
  totalPopups: number;
  onClose: () => void;
  onConfirm: () => void;
  onNext?: () => void;
  onPrevious?: () => void;
  onSkipAll?: () => void;
}

export const PopupNotification: React.FC<PopupNotificationProps> = ({
  popup,
  currentIndex,
  totalPopups,
  onClose,
  onConfirm,
  onNext,
  onPrevious,
  onSkipAll,
}) => {
  if (!popup) return null;

  const hasMultiplePopups = totalPopups > 1;
  const isFirstPopup = currentIndex === 0;
  const isLastPopup = currentIndex === totalPopups - 1;
  const progressPercent =
    totalPopups > 0 ? ((currentIndex + 1) / totalPopups) * 100 : 0;

  const getPopupIcon = (type = 'info') => {
    switch (type) {
      case 'celebration':
        return '🎉';
      case 'announcement':
        return '📢';
      case 'info':
        return 'ℹ️';
      default:
        return '📄';
    }
  };

  const getHeaderClass = (type = 'info') => {
    switch (type) {
      case 'celebration':
        return 'bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20';
      case 'announcement':
        return 'bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20';
      case 'info':
        return 'bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800/20 dark:to-gray-700/20';
      default:
        return 'bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800/20 dark:to-gray-700/20';
    }
  };

  return (
    <Modal
      title={
        <div
          className={`space-y-3 p-4 -m-6 mb-0 rounded-t-lg ${getHeaderClass(popup.type)}`}
        >
          <div className="flex items-center justify-between">
            <span className="text-lg font-semibold flex items-center gap-2">
              <span className="text-xl">{getPopupIcon(popup.type)}</span>
              {popup.title}
            </span>
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={onClose}
              className="flex items-center justify-center rounded-full w-8 h-8"
              data-testid={'close-popup'}
            />
          </div>

          {hasMultiplePopups && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm opacity-75">
                <span>
                  {currentIndex + 1} / {totalPopups}
                </span>
                <span className="text-xs">Duyuru {currentIndex + 1}</span>
              </div>
              <Progress
                percent={progressPercent}
                showInfo={false}
                strokeColor="#1677ff"
                strokeWidth={4}
              />
            </div>
          )}
        </div>
      }
      open={true}
      onCancel={onClose}
      footer={
        <div>
          <div className="border-t border-gray-200 dark:border-gray-700 -mx-6 mb-4"></div>

          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              {hasMultiplePopups && (
                <>
                  <Button
                    type="default"
                    icon={<LeftOutlined />}
                    onClick={onPrevious}
                    disabled={isFirstPopup}
                    size="small"
                    data-testid={'previous-button'}
                  >
                    Önceki
                  </Button>
                  <Button
                    type="default"
                    icon={<RightOutlined />}
                    onClick={onNext}
                    disabled={isLastPopup}
                    size="small"
                    data-testid={'next-button'}
                  >
                    Sonraki
                  </Button>
                </>
              )}
            </div>

            <div className="flex items-center space-x-2">
              {hasMultiplePopups && (
                <Button
                  type="default"
                  icon={<FastForwardOutlined />}
                  onClick={onSkipAll}
                  size="small"
                  data-testid={'skip-all-button'}
                >
                  Tümünü Atla
                </Button>
              )}
              <Button
                type="primary"
                onClick={onConfirm}
                data-testid={'done-button'}
              >
                {isLastPopup || !hasMultiplePopups ? 'Tamam' : 'Sonraki'}
              </Button>
            </div>
          </div>
        </div>
      }
      centered
      width={600}
      closeIcon={null}
      maskClosable={false}
      className="popup-notification-modal"
    >
      <div className="py-4">
        {popup.videoUrl && (
          <div className="mb-4">
            <div className="rounded-lg overflow-hidden shadow-md p-2 bg-gray-50">
              <iframe
                width="100%"
                height="315"
                src={popup.videoUrl}
                title={popup.title}
                allowFullScreen
                loading="lazy"
                className="rounded border-0"
              />
            </div>
          </div>
        )}

        {popup.content && (
          <div
            className="leading-relaxed"
            dangerouslySetInnerHTML={{ __html: popup.content }}
          />
        )}
      </div>
    </Modal>
  );
};
